import { type Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  defaultIfEmpty,
  EMPTY,
  first,
  map,
  type Observable,
  of,
  switchMap,
  tap,
  timeout,
} from 'rxjs';

import { type CanvasElementId } from '@blaze-runtime/engine-domain';
import { type CanvasElementInputBinding } from '@blaze-shared/elements-domain';

import type { ElementsFacade } from '../../+state/elements';
import type { RuntimeFacade } from '../../+state/runtime';

export interface DispatchValueBindingRefreshOptions {
  elementsFacade: ElementsFacade;
  runtimeFacade: RuntimeFacade;
  store: Store;
  elementId: CanvasElementId;
  timeoutMs: number;
  onTimeout: () => void;
  onError: (error: unknown) => void;
  bindingKey?: CanvasElementInputBinding;
  afterDispatch?: () => Observable<void>;
  visibleFields?: string[];
}

export function dispatchValueBindingRefresh({
  elementsFacade,
  runtimeFacade,
  store,
  elementId,
  timeoutMs,
  onTimeout,
  onError,
  bindingKey = 'value',
  afterDispatch,
  visibleFields,
}: DispatchValueBindingRefreshOptions): Observable<void> {
  return combineLatest({
    entities: elementsFacade.entities$,
    workflows: runtimeFacade.workflows$,
  }).pipe(
    first(),
    switchMap(({ entities, workflows }) =>
      elementsFacade
        .getUpdateBindingAction(
          { elementId, bindingKey, isNewDataSource: true, visibleFields },
          entities,
          workflows,
        )
        .pipe(
          timeout({
            first: timeoutMs,
            with: () => {
              onTimeout();
              return EMPTY;
            },
          }),
        ),
    ),
    tap((action) => store.dispatch(action)),
    switchMap(() => afterDispatch?.() ?? of(undefined)),
    defaultIfEmpty<void, undefined>(undefined),
    catchError((error) => {
      onError(error);
      return of(undefined);
    }),
    map((): void => undefined),
  );
}
